<template>
  <div :class="rootWrapperClass">
    <div class="player">
      <div class="header" v-if="live || this.currentVideo">
        <div class="artist-avatar">
          <img :src="artistAvatar">
        </div>
        <div class="now-playing">
          <div class="title">
            {{ contentTitle  }}
            <font-awesome-icon icon="check" class="added" v-if="inMyVideos" />
          </div>
          <div class="artist">
            {{ artistName }}
          </div>
        </div>
        <div class="live online" v-if="live && live.is_live && this.isOnline">
            <font-awesome-icon icon="circle" /> &nbsp; LIVE
        </div>
        <div class="live ready" v-if="live && live.is_live && !this.isOnline">
            <font-awesome-icon icon="spinner" spin/>
            WAITING FOR STREAM
        </div>
        <div class="live stopped" v-if="live && !live.is_live">
            STOPPED
        </div>
        <div class="control" v-if="!live">
          <font-awesome-icon icon="backward" class="ctl" v-if="orderedVideos" @click="previous" />
          <font-awesome-icon v-if="isPlaying" icon="pause" class="ctl" @click="pause" />
          <font-awesome-icon v-else icon="play" class="ctl"  @click="play" />
          <font-awesome-icon icon="forward" class="ctl" v-if="orderedVideos" @click="next" />
          <font-awesome-icon icon="plus" class="ctl add" :id="'playerAddToPlaylist' + currentVideo.id" v-if="currentVideo && isLoggedIn" />
          <AddToPlaylist :target="'playerAddToPlaylist' + currentVideo.id" :video="currentVideo" v-if="currentVideo && isLoggedIn" />
          <font-awesome-icon icon="random" :class="shuffleClass" @click="isShuffle = !isShuffle" v-if="orderedVideos" />
          <Report :video="currentVideo" v-if="currentVideo" label="Report video" class="ctl add" />
        </div>
        <div class="control" v-if="live">
          <Report :live="live" label="Report live stream" class="ctl add" />
          <font-awesome-icon icon="comments" :class="liveChatToggleClass" @click="liveChatVisible = !liveChatVisible" v-if="hasLiveChat" />
        </div>
      </div>
      <div class="custom-wrapper">
        <div ref="dacastPlayerWrapper" class="dacast-wrapper" :style="wrapperStyle"></div>
        <div class="custom-spinner hidden"></div>
        <div class="custom-big-play-button hidden" @click="play" @touchend="play"></div>
      </div>
    </div>
    <div class="playlist wide-flex" v-if="orderedVideos">
      <div class="header">
        <font-awesome-icon icon="random" :class="shuffleClass" @click="isShuffle = !isShuffle" />
      </div>
      <div class="videos">
        <div :class="videoCardClass(index)" v-for="(video, index) in orderedVideos" :key="video.id" @click="jumpToVideo(index)">
          <div class="thumb">
            <img :src="getThumbnail(video)">
            <div class="duration">
              {{ formatDuration(video) }}
            </div>
          </div>
          <div class="info">
            <div class="title">
              {{ video.title }}
            </div>
            <div class="artist">
              {{ video.artist.name }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <LiveChat v-if="live && live.is_live && liveChatVisible"
              :live="live"
              :canBan="canBan"
              @ban="banUser"
              @gotBanned="gotBanned">
    </LiveChat>
  </div>
</template>

<style scoped lang="scss">
  @import '@/assets/custom.scss';
  @import '~bootstrap/scss/mixins/_breakpoints.scss';

  .wrapper {
    display: flex;
    width: 100%;

    background: $dark;

    .player {
      display: flex;
      flex-direction: column;
      flex: 1;

      .header {
        background-color: $dark;
        background-image: url('/static/bg/player-header.png');
        background-size: cover;

        display: flex;
        padding: 0.5em;
        align-items: center;

        .artist-avatar {
          flex: 0;
          flex-basis: 24px;

          img {
            width: 48px;
            height: 48px;
            border-radius: 100%;
            margin-right: 0.5em;
          }
        }

        .now-playing {
          flex: 1;

          .title {
            font-weight: bold;
          }

          .artist {
            font-size: 0.8em;
            color: $steel;
          }
        }

        .live {
          &.online {
            background: $tomato;
            color: $white;
          }

          &.ready {
            background: $pale-gold;
            color: $dark;
          }

          &.stopped {
            background: $warm-grey;
            color: $white;
          }

          font-weight: bold;
          font-size: 0.8em;
          padding: 0.5em 1em;
          border-radius: 4px;
        }

        .control {
          .added {
            margin-left: 0.5em;
          }

          .ctl {
            margin-left: 0.5em;
            color: $blue-gray;

            &.active {
              color: $white;
            }

            &.add {
              margin-left: 1em;
            }

            &.shuffle.active {
              -webkit-text-fill-color: $aqua-marine;
              color: $aqua-marine;
            }
          }
           @include media-breakpoint-down(sm){
             .ctl {
               -webkit-text-fill-color: $blue-gray;
               color: $blue-gray;
               &.active {
                 -webkit-text-fill-color: $blue-gray;
                 color: $blue-gray;
               }
             }
           }

          @media (hover: hover) {
            .ctl {
              &.active:hover {
                color: $blue-gray;
              }
              &:hover {
                color: $white;
              }
            }
          }
        }
      }
    }
    $primary-color: rgb(220, 54, 129);
    $tertiary-color: rgba(44, 45, 76, 0.3);
    $tool-tip-color: rgba(203, 53, 144, 0.6);
    $white-color: #ffffff;
    $black-color: #000000;
    .custom-wrapper {
      position: relative;
    }
    .custom-spinner {
      position: absolute;
      display: block !important;
      width: 50px !important;
      height: 50px !important;
      border: 5px solid rgba(255, 255, 255, 0);
      border-top: 5px solid $primary-color;
      border-right: 5px solid $primary-color;
      border-left: 5px solid $primary-color;
      border-radius: 50%;
      top: 50% !important;
      left: 50% !important;
      margin-left: -25px;
      margin-top: -25px;
      animation: spin 1s linear infinite;
    }
    .custom-big-play-button {
      position: absolute;
      top: 50% !important;
      left: 50% !important;
      margin-left: -45px;
      margin-top: -45px;
      height: 90px;
      width: 90px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .custom-big-play-button:before {
      font-family: DaCast !important;
      content: "\f101" !important;
      font-size: 90px !important;
      font-weight: bold !important;
      font-style: normal !important;
      color: $primary-color;
      cursor: pointer;
    }
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .dacast-wrapper {
      background: $black-color !important;
      width: 100vw;
      height: 0;
      padding-top: 56.25%;

      /deep/ .theo-player-wrapper {
        margin: 0 auto;
        /* Workaround for DaCast player removing custom theme <style> tag clearing the defined theme.
         * If this happens, these will override the THEOPlayer yellow/white defaults.
         */

        .theo-primary-color, .theo-secondary-color, .vjs-control-bar {
          color: $primary-color !important;
          background: none !important;
        }

        .theo-primary-background {
          color:$primary-color !important;
          background-color: $tertiary-color !important;
        }

        .theo-tertiary-color {
          color: $tertiary-color !important
        }

        .theo-menu-item > .theo-primary-color {
          color:$primary-color !important
        }
        .theo-tertiary-background{
          color: $primary-color !important;
          background-color: $tertiary-color !important
        }
        .vjs-control:focus, .vjs-control:active {
          color: $primary-color !important;
        }
        .vjs-control:hover{
          color: $white-color !important;
        }
        .vjs-load-progress {
          background-color: $white-color !important;
        }
        .vjs-play-progress {
          background-color: $primary-color !important;
        }
        .vjs-play-progress:before {
          display: none;
        }
        .vjs-time-control {
          color: $primary-color !important;
          order: 2 !important;
          padding: 0 0.1rem !important;
          font-size: 0.8rem !important;
          line-height: 3.3 !important;
        }
        .vjs-time-control:hover, .vjs-current-time-display:hover, .vjs-time-divider:hover > div > span, .vjs-duration-display:hover {
          color: $primary-color !important;
        }
        .vjs-current-time {
          color: $primary-color !important;
        }
        .vjs-mute-control.vjs-vol-0:before, .vjs-mute-control.vjs-vol-1:before, .vjs-mute-control.vjs-vol-2:before, .vjs-mute-control:before {
          font-size: 1.2rem !important;
          line-height: 2.1 !important;
        }
        .vjs-progress-holder {
          margin: 0.3rem !important;
          background-color: $tertiary-color !important;
        }
        .vjs-volume-control {
          background-color: $tool-tip-color !important;
        }
        .videojs-custom-button {
          margin-top: 2px;
        }
        .dc-spinner, .dc-playback-prompts, .dc-big-play-button {
          display: none !important;
        }
        @include media-breakpoint-down(sm){
          .vjs-time-control{
            display: flex;
          }
          .vjs-duration {
            position: relative !important;
            top: auto;
            right: auto;
            margin-right: 0.3rem;
          }
          .vjs-control {
            width: 2em !important;
          }
          .vjs-control {
            -webkit-text-fill-color: $primary-color !important;
          }
          .vjs-control:focus, .vjs-control:active {
            -webkit-text-fill-color: $primary-color !important;
          }
          .vjs-progress-holder {
            margin-left: 0 !important;
          }
          .show-control {
            visibility: visible;
            opacity: 1 !important;
            -webkit-transition: visibility 1s, opacity 1s;
            -moz-transition: visibility 1s, opacity 1s;
            transition: visibility 1s, opacity 1s;
          }
          .hide-control {
            visibility: hidden;
            opacity: 0 !important;
            -webkit-transition: visibility 1s, opacity 1s;
            -moz-transition: visibility 1s, opacity 1s;
            transition: visibility 1s, opacity 1s;
          }
        }
      }

      @include media-breakpoint-down(md) {
        width: 100%;
      }
    }

    @include media-breakpoint-up(sm) {
      .dacast-wrapper {
        width: 100%;
        height: 320px;
      }
    }

    .playlist {
      display: flex;
      flex-direction: column;
      flex: 1;
      padding-left: 0.5em;
      max-width: 20em;

      .header {
        padding: 0.5em 1em 0.5em 0;
        text-align: right;

        .ctl {
          color: $blue-gray;

          &.active {
            color: $aqua-marine;
          }
        }
      }

      .videos {
        height: 0px;
        flex: 1 1 auto;
        flex-direction: column;
        overflow-y: scroll;

        &::-webkit-scrollbar {
          width: 10px;
          background-color: #49495b;
        }

        &::-webkit-scrollbar-track {
        }

        &::-webkit-scrollbar-thumb {
          border-radius: 5px;
          background-color: $steel;
          border: 2px solid #49495b;
        }

        .video-card {
          display: flex;
          font-size: 0.8em;
          padding: 5px;
          cursor: pointer;

          &.active {
            background-color: $dark-two;
          }

          .thumb {
            flex: 0 0;
            width: 120px;
            height: 68px;
            flex-basis: 120px;
            margin-right: 5px;
            margin-bottom: 5px;
            position: relative;

            display: flex;
            justify-content: center;
            align-items: center;

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }

            .duration {
              position: absolute;
              left: 5px;
              bottom: 5px;
              background: $black;
              color: $white;
              opacity: 0.68;
              padding: 3px 5px 3px 5px;
              font-size: 0.6em;
              border-radius: 2px;
            }
          }

          .info {
            display: none;
            .artist {
              font-size: 0.8em;
              color: $steel;
            }
          }

          @include media-breakpoint-up(lg) {
            .info {
              display: block;
            }
          }
        }
      }
    }
  }

  .wide-inline, .wide-flex {
    display: none !important;
  }

  @include media-breakpoint-up(lg) {
    .wide-inline {
      display: inline !important;
    }

    .wide-flex {
      display: flex !important;
    }

    .control .ctl.shuffle {
      display: none;
    }
  }

  @include media-breakpoint-down(lg) {
    .wrapper.live {
      flex-direction: column;

      /deep/ .live-chat {
        max-width: 100%;

        .messages {
          height: 9em;
        }
      }
    }
  }
</style>

<script>
import Vue from 'vue'
import FontAwesomeIcon from '@fortawesome/vue-fontawesome'

import { mapGetters } from 'vuex'

import shuffle from 'lodash/shuffle'
import { addPlaylistButtons, addVideoDuration, addPlaylistButtonsListener, removePlaylistButtonsListener } from '@/utils/dacast'

import LiveChat from './LiveChat'
import AddToPlaylist from './AddToPlaylist'
import Report from './Report'
import themeObject from './dacastPlayerTheme'
import { songHelpers } from '@/store/helpers'

export default {
  name: 'Player',
  components: {
    FontAwesomeIcon,
    LiveChat,
    AddToPlaylist,
    Report
  },
  model: {
    prop: 'video',
    event: 'change'
  },
  props: {
    pageUrl: String,
    video: Object,
    videos: Array,
    position: Number,
    live: Object,
    canBan: Boolean,
    once: Boolean,
    autoplay: Boolean
  },
  data () {
    return {
      currentVideo: null,
      orderedVideos: null,
      currentPosition: null,
      dacastPlayer: null,
      isPlaying: false,
      shouldResume: false,
      isOnline: false,
      statusCallbackId: null,
      livePingIntervalId: null,
      isShuffle: false,
      liveChatVisible: false,
      currentVideoRecorded: false,
      controlTimeOut: null
    }
  },
  computed: {
    contentId () {
      if (this.live && this.live.is_live) return this.live.dacast_content_id
      return this.currentVideo ? this.currentVideo.dacast_content_id : null
    },
    artistAvatar () {
      if (this.live && this.live.artist) return this.live.artist.avatar_image
      return (this.currentVideo && this.currentVideo.artist) ? this.currentVideo.artist.avatar_image : null
    },
    hasContent () {
      return !!this.contentId
    },
    contentTitle () {
      if (this.live) return this.live.title
      return this.currentVideo ? this.currentVideo.title : null
    },
    artistName () {
      if (this.live && this.live.artist) return this.live.artist.name
      return (this.currentVideo && this.currentVideo.artist) ? this.currentVideo.artist.name : null
    },
    wrapperStyle () {
      let style = {}

      if (!this.dacastPlayer) {
        let img = null
        if (this.live && this.live.promo_image) img = this.live.promo_image
        if (this.currentVideo && this.currentVideo.video_thumbnail) img = this.currentVideo.video_thumbnail
        if (img) {
          style['background-image'] = 'url(' + img + ')'
          style['background-position'] = 'center'
          style['background-size'] = 'cover'
          style['background-repeat'] = 'no-repeat'
        }
      }

      return style
    },
    rootWrapperClass () {
      let cls = ['wrapper']
      if (this.live) cls.push('live')
      return cls
    },
    myVideos () {
      if (!this.isLoggedIn) return null
      return this.$store.getters['profile/currentUser'].default_playlists.my_videos
    },
    inMyVideos () {
      if (!this.currentVideo || !this.myVideos) return false
      return this.myVideos.video_ids.indexOf(this.currentVideo.id) !== -1
    },
    shuffleClass () {
      let cls = ['ctl', 'shuffle']
      if (this.isShuffle) cls.push('active')
      return cls
    },
    liveChatToggleClass () {
      let cls = ['wide-inline', 'ctl', 'add']
      if (this.liveChatVisible) cls.push('active')
      return cls
    },
    hasLiveChat () {
      return this.live && this.live.is_live && this.live.chat_type !== 'disabled'
    },
    ...mapGetters('auth', ['isLoggedIn'])
  },
  methods: {
    initComponent () {
      if (!this.orderedVideos) this.orderedVideos = this.videos

      if (this.live) {
        this.disposePlayer()
        if (this.live.is_live) {
          this.initPlayer()
        }

        this.liveChatVisible = this.live.chat_type !== 'disabled'
        return
      }
      if (this.currentVideo === this.video) return
      console.log('[player] initializing component')
      this.currentVideo = this.video
      this.currentPosition = (this.position !== undefined) ? this.position : this.findVideoPosition()
      this.isPlaying = false
      this.initPlayer()
    },
    recalculatePosition () {
      this.orderedVideos = this.videos
      if (this.isShuffle) this.shuffle()
      this.currentPosition = this.findVideoPosition()
    },
    reshuffle () {
      if (this.isShuffle) this.shuffle()
      this.currentPosition = this.findVideoPosition()
    },
    hideBigPlayButton () {
      const bigPlayButton = document.querySelector('.custom-big-play-button')
      bigPlayButton.classList.add('hidden')
    },
    showBigPlayButton () {
      const bigPlayButton = document.querySelector('.custom-big-play-button')
      bigPlayButton.classList.remove('hidden')
    },
    hidePoster () {
      const posterInterval = setInterval(() => {
        const theoPoster = document.querySelector('.theoplayer-poster')
        if (theoPoster) {
          theoPoster.classList.add('hidden')
          clearInterval(posterInterval)
        }
      }, 100)
    },
    showSpinner () {
      this.hidePoster()
      const customSpinner = document.querySelector('.custom-spinner')
      customSpinner.classList.remove('hidden')
    },
    hideSpinner () {
      document.querySelector('.custom-spinner').classList.add('hidden')
    },
    initPlayer () {
      const miniPlayer = this.$store.getters.miniPlayer
      const miniPlayerVideo = miniPlayer.getCurrentVideo()

      if (miniPlayerVideo && this.video && miniPlayerVideo.id === this.video.id && this.once) {
        console.log('[player] created in once mode for content_id=' + this.contentId + ' that\'s already playing, ignoring')
        return
      }

      let ctx = miniPlayer.releasePlayer(this.pageUrl)

      if (ctx) {
        this.disposePlayer()
        this.dacastPlayer = ctx.player
        this.currentVideo = ctx.video
        this.currentPosition = ctx.position
        this.isPlaying = ctx.player.playing()
        addPlaylistButtonsListener(this.dacastPlayer, this.previous, this.next)
        this.$refs.dacastPlayerWrapper.appendChild(this.dacastPlayer.getElement())
        Vue.nextTick(this.resizePlayer)
      } else {
        console.log(
          '[player] creating new dacast player instance for content_id=' +
          this.contentId +
          ' - ts: ' + (new Date()).toISOString()
        )
        this.shouldResume = false
        if (this.dacastPlayer) {
          this.dacastPlayer.stop()
          this.dacastPlayer.loadVideo(this.contentId)
          this.showSpinner()
          const playInterval = setInterval(() => {
            if (this.dacastPlayer.getContentInfo().contentId === this.contentId && this.dacastPlayer.duration()) {
              this.play()
              clearInterval(playInterval)
            }
          }, 300)
          return
        } else {
          this.dacastPlayer = window.dacast(this.contentId, this.$refs.dacastPlayerWrapper, {
            player: 'theo',
            width: '568',
            height: '320',
            theme: themeObject
          })
        }
        this.isOnline = false
        this.$emit('online', this.isOnline)
        if (this.autoplay) {
          this.showSpinner()
        } else {
          this.showBigPlayButton()
          this.hideSpinner()
        }
      }

      let player = this.dacastPlayer

      const onceReady = () => {
        player.off('ready', onceReady)

        if (this.isPlaying || (this.autoplay && !this.$store.getters.miniPlayer.isPlaying)) {
          console.log('[player] auto-starting playback')
          player.play()
        } else {
          console.log('[player] not auto-starting playback; isPlaying=' + this.isPlaying +
            '; autoplay=' + this.autoplay +
            '; miniPlayer.isPlaying=' + this.$store.getters.miniPlayer.isPlaying
          )
        }
        addVideoDuration(player)
        if (this.orderedVideos) {
          addPlaylistButtons(player, this.previous, this.next)
        }
        this.resizePlayer()
        this.currentVideoRecorded = false
        this.playPauseOnTouch()
        this.showControl()
      }
      if (player) {
        player.on('play', this.onPlay)
        player.on('pause', this.onPause)
        player.on('complete', this.onComplete)
        player.on('error', this.onError)
        player.on('ready', onceReady)
      }

      this.statusCallbackId = window.setInterval(this.onStatus, 2000)
      if (this.live) {
        this.livePingIntervalId = window.setInterval(this.ping, 10000)
        this.ping()
      }
    },
    isControlShown () {
      const controlBar = document.querySelector('.vjs-play-control').closest('.vjs-control-bar')
      return controlBar.classList.contains('show-control')
    },
    showControl () {
      if (this.isControlShown()) {
        clearTimeout(this.controlTimeOut)
        this.controlTimeOut = setTimeout(this.hideControl, 4000)
        return
      }
      const controlBar = document.querySelector('.vjs-play-control').closest('.vjs-control-bar')
      controlBar.classList.remove('hide-control')
      controlBar.classList.add('show-control')
      this.controlTimeOut = setTimeout(this.hideControl, 4000)
    },
    hideControl () {
      if (this.dacastPlayer && this.dacastPlayer.paused()) {
        clearTimeout(this.controlTimeOut)
        this.controlTimeOut = setTimeout(this.hideControl, 4000)
        return
      }
      const controlBar = document.querySelector('.vjs-play-control').closest('.vjs-control-bar')
      controlBar.classList.remove('show-control')
      controlBar.classList.add('hide-control')
    },
    playPauseOnTouch () {
      const el = document.querySelector('.theo-player-wrapper:first-child>div')
      el.addEventListener('touchend', function (e) {
        if (el !== e.target.parentNode) return
        this.playPause()
      }.bind(this), false)
    },
    playPause () {
      if (!this.dacastPlayer || !this.dacastPlayer.ready()) return
      if (this.isControlShown()) {
        this.dacastPlayer.playPause()
      }
      this.showControl()
    },
    findVideoPosition () {
      if (this.video && this.orderedVideos) {
        for (let i = 0; i < this.orderedVideos.length; i++) {
          if (this.video.id === this.orderedVideos[i].id) {
            return i
          }
        }
      }
      return this.position || 0
    },
    onPlay () {
      console.log('[player] playing')
      this.hideBigPlayButton()
      this.isPlaying = true
      if (this.live) {
        this.isOnline = true
        this.$emit('online', this.isOnline)
      }
      this.$eventHub.$emit('stop-playback', this)
      const loadedInterval = setInterval(() => {
        const loaded = document.querySelector('.vjs-load-progress').style.width
        if (parseFloat(loaded.replace('%', '')) > 0.01 && this.dacastPlayer.currentTime() < 0.01) {
          this.pause()
          this.hideSpinner()
          clearInterval(loadedInterval)
        } else if (this.dacastPlayer.currentTime() > 0.01) {
          clearInterval(loadedInterval)
        }
      }, 300)
      const spinnerInterval = setInterval(() => {
        if (this.dacastPlayer.currentTime() > 0.01) {
          this.hideSpinner()
          clearInterval(spinnerInterval)
        }
      }, 100)
    },
    onPause () {
      if (this.shouldResume) {
        console.log('[player] auto-resuming after player handover')
        this.shouldResume = false
        window.setTimeout(() => this.dacastPlayer.play(), 0)
      } else if (this.live) {
        console.log('[player:live] disallowing pause')
        window.setTimeout(() => this.dacastPlayer.play(), 0)
      } else {
        console.log('[player] paused')
        this.isPlaying = false
      }
    },
    onComplete () {
      console.log('[player] video complete')
      this.isPlaying = false
      if (!this.once) Vue.nextTick(this.next)
    },
    onError (err) {
      console.log('[player] error', err.dc_type)
      this.isPlaying = false
      if (this.live) {
        this.isOnline = false
        this.$emit('online', this.isOnline)
      } else {
        // Possibly the DaCast token expired, let's try to get a new one
        if (err.dc_type === 'VIDEO_IS_OFFLINE') {
          this.refreshToken()
        }
      }
    },
    recordView () {
      if (!this.currentVideo || this.currentVideoRecorded) return

      this.currentVideoRecorded = true
      this.$store.dispatch('song/recordView', {id: this.currentVideo.id})
      if (this.isLoggedIn) this.$store.dispatch('playlist/addToHistory', this.video.id)

      // this is non-authoritative, just so we don't need to reload fresh stats from the server
      this.currentVideo.view_count = this.currentVideo.view_count + 1
    },
    async refreshToken () {
      if (!this.currentVideo || !this.dacastPlayer) return

      const playerInstance = this.dacastPlayer.getPlayerInstance()
      if (!playerInstance) return
      this.initPlayer()
    },
    onStatus () {
      if (!this.dacastPlayer) return
      const instance = this.dacastPlayer.getPlayerInstance()
      if (!instance) return

      this.isOnline = instance.network.online
      this.$emit('online', this.isOnline)
      if (this.dacastPlayer.currentTime() > 10) this.recordView()
    },
    play () {
      if (!this.dacastPlayer) return
      if (!this.autoplay) {
        this.hideBigPlayButton()
        this.showSpinner()
      }
      this.dacastPlayer.play()
    },
    pause () {
      if (!this.dacastPlayer) return
      this.dacastPlayer.pause()
    },
    disposePlayer () {
      if (!this.dacastPlayer) return

      try { this.dacastPlayer.dispose() } catch (e) {}
      this.dacastPlayer = null

      if (this.statusCallbackId) {
        window.clearInterval(this.statusCallbackId)
        this.statusCallbackId = null
      }

      if (this.livePingIntervalId) {
        window.clearInterval(this.livePingIntervalId)
        this.livePingIntervalId = null
      }
    },
    async jumpToVideo (pos) {
      if (!this.orderedVideos || !this.orderedVideos.length) return
      this.currentPosition = (pos + this.orderedVideos.length) % this.orderedVideos.length
      this.currentVideo = this.orderedVideos[this.currentPosition]
      this.currentVideoRecorded = false
      console.log('[player] jumping to position ' + this.currentPosition)
      this.initPlayer()
      this.$emit('update:position', this.currentPosition)
      this.$emit('change', this.currentVideo)
    },
    next () {
      if (this.isShuffle && this.orderedVideos && this.currentPosition === (this.orderedVideos.length - 1)) {
        this.shuffle()
        this.jumpToVideo(0)
      } else {
        this.jumpToVideo(this.currentPosition + 1)
      }
    },
    previous () {
      this.jumpToVideo(this.currentPosition - 1)
    },
    shuffle () {
      this.orderedVideos = shuffle(this.videos)
    },
    videoCardClass (pos) {
      return {
        'video-card': true,
        'active': pos === this.currentPosition
      }
    },
    resizePlayer () {
      const wrap = this.$refs.dacastPlayerWrapper
      if (!wrap) return

      const playerEl = wrap.firstElementChild
      if (!playerEl) return

      const theoPlayer = playerEl.firstElementChild
      if (!theoPlayer) return

      const wrapSize = wrap.getBoundingClientRect()
      theoPlayer.style.width = '100%'
      theoPlayer.style.height = '100%'
      theoPlayer.style.position = 'static'
      playerEl.style.width = '100%'
      playerEl.style.height = '100%'
      wrap.style.paddingTop = '0px'
      wrap.style.height = Math.floor(wrapSize.width * 320 / 568) + 'px'
    },
    banUser (user) {
      this.$emit('ban', user)
    },
    gotBanned () {
      this.$emit('gotBanned')
    },
    onStopPlayback (other) {
      if (this === other) return
      this.pause()
    },
    async ping () {
      if (!this.live) return

      const response = await this.$store.dispatch('live/pingLiveEvent', { id: this.live.id })
      if (!response) return

      if (response.members) {
        this.$emit('members', response.members)
      }
      if (response.is_cancelled) {
        this.$emit('liveStreamCancelled')
        this.disposePlayer()
      } else if (response.has_ended) {
        this.$emit('liveStreamEnd')
        this.disposePlayer()
      }
    },
    ...songHelpers
  },
  watch: {
    'live': 'initComponent',
    'video': 'initComponent',
    'videos': 'recalculatePosition',
    'isShuffle': 'reshuffle'
  },
  created () {
    this.$eventHub.$on('stop-playback', this.onStopPlayback)
  },
  mounted () {
    this.initComponent()
    this.resizePlayer()
    window.addEventListener('resize', this.resizePlayer)
  },
  updated () {
    this.resizePlayer()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resizePlayer)
    this.$eventHub.$off('stop-playback', this.onStopPlayback)
    removePlaylistButtonsListener(this.dacastPlayer, this.previous, this.next)

    if (!this.isPlaying || this.once) {
      this.disposePlayer()
      return
    }

    let miniPlayer = this.$store.getters.miniPlayer

    this.dacastPlayer.off('play', this.onPlay)
    this.dacastPlayer.off('pause', this.onPause)
    this.dacastPlayer.off('complete', this.onComplete)
    this.dacastPlayer.off('error', this.onError)
    miniPlayer.receivePlayer({
      player: this.dacastPlayer,
      video: this.video,
      live: this.live,
      videos: this.orderedVideos,
      position: this.currentPosition,
      videoRoute: this.pageUrl
    })

    if (this.statusCallbackId) {
      window.clearInterval(this.statusCallbackId)
      this.statusCallbackId = null
    }

    if (this.livePingIntervalId) {
      window.clearInterval(this.livePingIntervalId)
      this.livePingIntervalId = null
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/widgets/Player.vue